import React, { useState } from "react";
import { Users, Shield, BarChart2, FileDown } from "lucide-react";
import TabelaClassificacaoGeral from "../components/TabelaGeral";
import StatCard from "../components/StatCard";
import { getEscudoUrl } from "../api/ligaApi";
import CustomDropdown from "../components/CustomDropdown";

const Dashboard = ({
  ligaData,
  isAdmin,
  handleGerarPDF,
  handleGerarPDFGeral,
}) => {
  // Garante que os dados sejam sempre arrays para evitar erros
  const duplas = ligaData?.duplas || [];
  const rodadas = ligaData?.rodadas || [];

  const [selectedRodada, setSelectedRodada] = useState(1);

  const rodadasDisponiveis = [...new Set(rodadas.map((r) => r.numero))].sort(
    (a, b) => a - b
  );
  if (rodadasDisponiveis.length === 0) {
    rodadasDisponiveis.push(1);
  }
  const dropdownOptions = rodadasDisponiveis.map((num) => ({
    value: num,
    label: `Rodada ${num}`,
  }));

  const getPontosPorTimeNaRodada = (timeId, rodadaNum) => {
    const ponto = rodadas.find(
      (r) =>
        String(r.numero) == String(rodadaNum) &&
        r.time_id &&
        r.time_id.toString() === timeId.toString()
    );
    return ponto ? ponto.pontos : 0;
  };

  const getPontosPorDuplaNaRodada = (dupla, rodadaNum) =>
    (dupla.times || []).reduce(
      // Verificação de segurança
      (total, time) => total + getPontosPorTimeNaRodada(time.id, rodadaNum),
      0
    );

  const duplasPorRodada = [...duplas]
    .map((dupla) => ({
      ...dupla,
      pontosNaRodada: getPontosPorDuplaNaRodada(dupla, selectedRodada),
    }))
    .sort((a, b) => b.pontosNaRodada - a.pontosNaRodada);

  const totalTimes = duplas.reduce(
    (acc, dupla) => acc + (dupla.times?.length || 0),
    0
  );
  const totalRodadasFinalizadas = [
    ...new Set(rodadas.filter((r) => r.pontos != null).map((r) => r.numero)),
  ].length;

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-teal-400">
        Painel do Desafio
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          icon={<Users size={24} />}
          title="Duplas na Disputa"
          value={duplas.length}
          color="bg-blue-500"
        />
        <StatCard
          icon={<Shield size={24} />}
          title="Total de Times"
          value={totalTimes}
          color="bg-green-500"
        />
        <StatCard
          icon={<BarChart2 size={24} />}
          title="Rodadas Finalizadas"
          value={totalRodadasFinalizadas}
          color="bg-yellow-500"
        />
      </div>

      <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg mb-8">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 gap-4">
          <h3 className="text-xl font-bold text-white">
            Classificação Geral Acumulada
          </h3>
          {isAdmin && (
            <button
              onClick={handleGerarPDFGeral}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <FileDown size={18} className="mr-2" />
              Gerar PDF Geral
            </button>
          )}
        </div>
        <TabelaClassificacaoGeral duplas={duplas} rodadasData={rodadas} />
      </div>

      <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
          <h3 className="text-xl font-bold text-white">Detalhes da Rodada</h3>
          <div className="flex items-center space-x-4 w-full sm:w-auto">
            <CustomDropdown
              options={dropdownOptions}
              value={selectedRodada}
              onChange={(value) => setSelectedRodada(value)}
            />
            {isAdmin && (
              <button
                onClick={() => handleGerarPDF(selectedRodada)}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors text-sm sm:text-base justify-center"
              >
                <FileDown size={18} className="mr-2" />
                <span>PDF</span>
              </button>
            )}
          </div>
        </div>
        <div className="space-y-6">
          {duplasPorRodada.map((dupla) => (
            <div key={dupla.id} className="bg-gray-900/70 p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                <div className="flex items-center gap-3">
                  <img
                    src={getEscudoUrl(dupla.logo_url) || "/pair-icon.png"}
                    alt=""
                    className="w-10 h-10 rounded-full object-contain bg-gray-700"
                  />
                  <h4 className="text-lg font-bold text-teal-300 mb-2 sm:mb-0">
                    {dupla.nome}
                  </h4>
                </div>
                <span className="text-2xl font-bold text-white">
                  {dupla.pontosNaRodada.toFixed(2)} pts
                </span>
              </div>
              <div className="space-y-2">
                {/* LINHA CORRIGIDA E MAIS ROBUSTA ABAIXO */}
                {(dupla.times || [])
                  .sort((a, b) =>
                    (a?.nome_time || "").localeCompare(b?.nome_time || "")
                  )
                  .map((time) => (
                    <div
                      key={time.id}
                      className="bg-gray-700/50 p-3 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <img
                          src={getEscudoUrl(time.escudo_url)}
                          alt={`Escudo do ${time.nome_time}`}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-3"
                        />
                        <div>
                          <p className="font-bold text-white text-sm sm:text-base flex items-center">
                            {time.nome_time}
                            {time.is_captain && (
                              <span className="ml-2 text-yellow-400 font-bold text-xs">
                                (C)
                              </span>
                            )}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-400">
                            {time.nome_cartoleiro}
                          </p>
                        </div>
                      </div>
                      <p className="text-base sm:text-lg font-bold text-teal-400">
                        {getPontosPorTimeNaRodada(
                          time.id,
                          selectedRodada
                        ).toFixed(2)}{" "}
                        pts
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
